"use client";

import React from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";

import { useModal } from "@/client/shared/hooks/useModal";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContainer,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Typography } from "@/components/ui/typography";
import endpoints from "@/constants/endpoints";
import { PermissionErrorAction } from "@/errors/permission.errors";
import { doIt } from "@/utils/doIt";

//

interface PermissionErrorItem {
  title: string;
  description?: string;
  benefits: string[];
  primaryButtonText: string;
  image: string;
  redirectTo?: string;
}

const modalContent: Record<PermissionErrorAction, PermissionErrorItem> = {
  UPGRADE_SUBSCRIPTION: {
    title: "Обновите тариф",
    description: "Повысьте тариф, чтобы получить больше возможностей сервиса.",
    benefits: ["Бот цен", "Несколько магазинов", "Основные функции", "Полная аналитика"],
    primaryButtonText: "Обновить тариф",
    image: "/images/diamond.gif",
    redirectTo: endpoints.findPath("dashboard.billing"),
  },
  UP_ROLE: {
    title: "Недостаточно прав доступа",
    benefits: [],
    primaryButtonText: "Ок",
    image: "/images/block.gif",
  },
  LIMIT: {
    title: "Лимит исчерпан",
    description: "Повысьте тариф, чтобы получить больше лимитов.",
    benefits: [],
    primaryButtonText: "Обновить тариф",
    image: "/images/diamond.gif",
    redirectTo: endpoints.findPath("dashboard.billing"),
  },
};

//

export interface PermissionNoticeModalProps {
  action: PermissionErrorAction;
  message: string;
}

//

export function PermissionNoticeModal() {
  const open = useModal((s) => s.isOpen("permissionNotice"));
  const close = useModal((s) => s.close);
  const data = useModal((s) => s.getData("permissionNotice"));

  return (
    <Dialog open={open} onOpenChange={close}>
      <PermissionNoticeContent onClose={close} data={data} />
    </Dialog>
  );
}

//

export interface PermissionNoticeContentProps {
  data: PermissionNoticeModalProps | undefined;
  onClose?: () => void;
}

export const PermissionNoticeContent: React.FC<PermissionNoticeContentProps> = ({
  data,
  onClose,
}) => {
  const router = useRouter();

  const handlePrimaryAction = (data: PermissionNoticeModalProps) => {
    const { action } = data;
    const { redirectTo } = modalContent[action];

    if (redirectTo) {
      router.push(redirectTo);
    }

    if (onClose) onClose();
  };

  return (
    <DialogContent className="max-w-xs" noPadding>
      {doIt(() => {
        if (!data) return;
        const { action, message } = data;
        const content = modalContent[action];
        return (
          <DialogContainer>
            <DialogHeader className="items-center text-center">
              <Image alt="" src={content.image} width={100} height={100} />
              <DialogTitle>{content.title}</DialogTitle>
            </DialogHeader>

            <div className="mt-4 space-y-3 text-center">
              <Typography muted size="p">
                {content.description ?? message}
              </Typography>
              {content.benefits.length > 0 && (
                <div className="flex flex-wrap justify-center gap-2">
                  {content.benefits.map((benefit, index) => (
                    <Typography
                      key={index}
                      bold
                      sm
                      className="rounded-full bg-blue-100 px-2 py-1 text-blue-500"
                    >
                      {benefit}
                    </Typography>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-6 flex gap-2">
              {onClose && (
                <Button variant="outline" onClick={onClose}>
                  Закрыть
                </Button>
              )}
              <Button className="flex-1" onClick={() => handlePrimaryAction(data)}>
                {content.primaryButtonText}
              </Button>
            </div>
          </DialogContainer>
        );
      })}
    </DialogContent>
  );
};
